import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import type { FiscalPeriodRow } from './fiscalTimePeriods';

export interface QuarterRange {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD, inclusive
}

export interface QuarterBounds {
  current: QuarterRange | null;
  next: QuarterRange | null;
  /** End of the fiscal year that contains today. */
  yearEnd: string | null;
}

/** Current and next fiscal quarter, plus the fiscal year end, from Salesforce Period rows. */
export function quarterBounds(periods: FiscalPeriodRow[], today: Date = new Date()): QuarterBounds {
  const day = format(today, 'yyyy-MM-dd');
  const quarters = periods.filter((p) => p.type === 'Quarter').sort((a, b) => a.start.localeCompare(b.start));
  const current = quarters.find((p) => p.start <= day && day <= p.end) ?? null;
  const next = current ? quarters.find((p) => p.start > current.end) ?? null : null;
  const year = periods.find((p) => p.type === 'Year' && p.start <= day && day <= p.end) ?? null;
  return {
    current: current && { start: current.start, end: current.end },
    next: next && { start: next.start, end: next.end },
    yearEnd: year?.end ?? null,
  };
}

const addDaysTo = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const iso = (d: Date) => format(d, 'yyyy-MM-dd');

/** Cisco's fiscal year starts the Sunday after the last Saturday of July. */
const fiscalYearStart = (year: number): Date => {
  const d = new Date(year, 6, 31);
  while (d.getDay() !== 6) d.setDate(d.getDate() - 1);
  return addDaysTo(d, 1);
};

/**
 * The fiscal calendar worked out locally (13-week quarters, the last one absorbing a 53rd week), for when the
 * Salesforce `Period` lookup is unavailable. Matches Cisco's calendar, e.g. FY27 Q2 is Oct 25, 2026 – Jan 23, 2027.
 */
export function estimateQuarterBounds(today: Date = new Date()): QuarterBounds {
  let start = fiscalYearStart(today.getFullYear());
  if (start > today) start = fiscalYearStart(today.getFullYear() - 1);
  const nextYearStart = fiscalYearStart(start.getFullYear() + 1);
  const quarter = (i: number, fyStart: Date, fyNext: Date): QuarterRange => ({
    start: iso(addDaysTo(fyStart, 91 * i)),
    end: iso(i < 3 ? addDaysTo(fyStart, 91 * (i + 1) - 1) : addDaysTo(fyNext, -1)),
  });
  const day = iso(today);
  const index = [0, 1, 2, 3].find((i) => day <= quarter(i, start, nextYearStart).end) ?? 3;
  const current = quarter(index, start, nextYearStart);
  const next =
    index < 3
      ? quarter(index + 1, start, nextYearStart)
      : { start: iso(nextYearStart), end: iso(addDaysTo(nextYearStart, 90)) };
  return { current, next, yearEnd: iso(addDaysTo(nextYearStart, -1)) };
}

/** "Q2 FY27" for the fiscal quarter that starts on `startIso`. */
export function fiscalQuarterLabel(startIso: string): string {
  const d = parseISO(startIso);
  let fyStart = fiscalYearStart(d.getFullYear());
  if (fyStart > d) fyStart = fiscalYearStart(d.getFullYear() - 1);
  const q = Math.min(4, Math.floor(differenceInCalendarDays(d, fyStart) / 91) + 1);
  return `Q${q} FY${String((fyStart.getFullYear() + 1) % 100).padStart(2, '0')}`;
}
